import BaseApi, { API_VERSION, ServerListResponse } from '@lumapps/base-api';

import { RESOURCE_PATH } from '../constants';
import { filterReactionTypes } from '../utils/filterReactionTypes';
import { updateReactionState } from '../utils/updateReactionState';
import {
    REACTION_TYPES,
    ReactionCounts,
    ReactionDetails,
    ReactionListParams,
    ReactionResourceRef,
    ReactionState,
    ReactionType,
    ResourceReactionParams,
    UserReactions,
} from './types';

const api = new BaseApi({ path: '', version: API_VERSION.v2 });

/** Add a reaction on a resource */
export const addReaction = async ({ resourceType, resourceId, type }: ResourceReactionParams) => {
    const { data } = await api.put<ReactionDetails>(`${RESOURCE_PATH[resourceType]}/${resourceId}/reactions`, { type });
    return data;
};

/** Delete a reaction on a resource */
export const deleteReaction = async ({ resourceType, resourceId, type }: ResourceReactionParams) => {
    const { data } = await api.delete<ReactionDetails>(`${RESOURCE_PATH[resourceType]}/${resourceId}/reactions`, {
        data: {
            type,
        },
    });
    return data;
};

export const getReactionStatus = async ({ resourceType, resourceId }: ReactionResourceRef, signal?: AbortSignal) => {
    const { data } = await api.get<ReactionState>(`${RESOURCE_PATH[resourceType]}/${resourceId}/reactions/summary`, {
        signal,
    });
    // TODO: CMS-1169 clean this once the "like" has been removed from the reaction endpoint reactionType, and return the ReactionState from the backend.
    return {
        reactionCounts: {
            total: data.reactionCounts.total,
            details: data.reactionCounts.details.filter(({ type }) => REACTION_TYPES.includes(type)),
        },
        currentUserReactions: filterReactionTypes(data.currentUserReactions),
    };
};

/** List reactions by users */
export const listUserReactions = async (
    { resourceType, resourceId }: ReactionResourceRef,
    params: ReactionListParams,
) => {
    const { data } = await api.get<ServerListResponse<UserReactions>>(
        `${RESOURCE_PATH[resourceType]}/${resourceId}/reactions/users`,
        {
            params,
        },
    );

    // TODO: CMS-1169 clean this once the "like" has been removed from the reaction endpoint reactionType, and return the ReactionState from the backend.
    return {
        ...data,
        items: data.items.map((userReaction) => ({
            ...userReaction,
            reactions: filterReactionTypes(userReaction.reactions),
        })),
    };
};

/** Toggle reaction */
export const fetchToggleReaction = async (
    resourceRef: ReactionResourceRef,
    reaction: ReactionType,
    reactionCounts: ReactionCounts,
    currentUserReactions: ReactionType[],
): Promise<ReactionState> => {
    const toDelete = currentUserReactions.includes(reaction);
    const call = toDelete ? deleteReaction : addReaction;
    const reactionDetails = await call({ ...resourceRef, type: reaction });

    return updateReactionState({ reactionCounts, currentUserReactions }, reaction, reactionDetails);
};
